import { useState, useEffect } from "react";
import { RefreshCw, Plus, X } from "lucide-react";
import axios from "axios";
import { decryptRates } from "../utils/crypto";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Flag/icon paths - using local files from /flags/ folder
const getCurrencyFlag = (code) => {
  const flags = {
    // Crypto (SVG/WebP)
    BTC: "/flags/BTC.svg",
    ETH: "/flags/ETH.svg",
    USDT: "/flags/USDT.webp",
    ADA: "/flags/ADA.svg",
    SOL: "/flags/SOL.svg",
    TRX: "/flags/TRX.webp",
    // Fiat (PNG)
    USD: "/flags/USD.png",
    EUR: "/flags/EUR.png",
    GBP: "/flags/GBP.png",
    CAD: "/flags/CAD.png",
    MXN: "/flags/MXN.png",
    JPY: "/flags/JPY.png",
    AUD: "/flags/AUD.png",
    CHF: "/flags/CHF.png",
    CNY: "/flags/CNY.png",
    INR: "/flags/INR.png",
    AED: "/flags/AED.png",
    ARS: "/flags/ARS.png",
    BRL: "/flags/BRL.png",
    CLP: "/flags/CLP.png",
    CRC: "/flags/CRC.png",
    CZK: "/flags/CZK.png",
    DKK: "/flags/DKK.png",
    EGP: "/flags/EGP.png",
    FJD: "/flags/FJD.png",
    HKD: "/flags/HKD.png",
    HUF: "/flags/HUF.png",
    IDR: "/flags/IDR.png",
    ILS: "/flags/ILS.png",
    KRW: "/flags/KRW.png",
    MYR: "/flags/MYR.png",
    NOK: "/flags/NOK.png",
    NZD: "/flags/NZD.png",
    PEN: "/flags/PEN.png",
    PHP: "/flags/PHP.png",
    PLN: "/flags/PLN.png",
    SAR: "/flags/SAR.png",
    SCP: "/flags/SCP.png",
    SEK: "/flags/SEK.png",
    SGD: "/flags/SGD.png",
    THB: "/flags/THB.png",
    TRY: "/flags/TRY.png",
    TWD: "/flags/TWD.png",
    ZAR: "/flags/ZAR.png",
    IQD: "/flags/IQD.png",
    VND: "/flags/VND.png",
  };
  return flags[code] || null;
};

// Default visible currencies
const DEFAULT_CRYPTO = ["BTC", "USDT", "ETH"];
const DEFAULT_FIAT = ["USD", "EUR", "GBP"];

export default function RateBoard() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingType, setAddingType] = useState(null);
  const [addedCurrencies, setAddedCurrencies] = useState([]);

  useEffect(() => { 
    fetchRates();
    const interval = setInterval(fetchRates, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchRates = async () => {
    setLoading(true);
    try {
      // Use encrypted endpoint
      const response = await axios.get(`${API}/x7k9`);
      const decryptedRates = decryptRates(response.data.d);
      setRates(decryptedRates);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Error fetching rates:", error);
    } finally {
      setLoading(false);
    }
  };

  // Mirrors the CRM rate board: CAD per unit for everything.
  // Sub-1 rates keep 6 decimals so small currencies stay precise.
  const formatRate = (rate, code) => {
    if (rate >= 1000) return rate.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (rate < 1) return rate.toFixed(6);
    return rate.toFixed(4);
  };

  // Separate crypto and fiat, exclude CAD (base currency)
  const allCrypto = rates.filter(r => r.is_crypto);
  const allFiat = rates.filter(r => !r.is_crypto && r.currency_code !== 'CAD');

  // Sort fiat to put default ones first in correct order
  const sortedFiat = [...allFiat].sort((a, b) => {
    const aIndex = DEFAULT_FIAT.indexOf(a.currency_code);
    const bIndex = DEFAULT_FIAT.indexOf(b.currency_code);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return 0;
  });

  // Sort crypto to put default ones first
  const sortedCrypto = [...allCrypto].sort((a, b) => {
    const aIndex = DEFAULT_CRYPTO.indexOf(a.currency_code);
    const bIndex = DEFAULT_CRYPTO.indexOf(b.currency_code);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return 0;
  });

  // Default visible currencies
  const defaultVisibleCrypto = sortedCrypto.filter(r => DEFAULT_CRYPTO.includes(r.currency_code));
  const defaultVisibleFiat = sortedFiat.filter(r => DEFAULT_FIAT.includes(r.currency_code));

  // Hidden currencies (all others not in default list)
  const hiddenCrypto = sortedCrypto.filter(r => !DEFAULT_CRYPTO.includes(r.currency_code) && !addedCurrencies.includes(r.currency_code));
  const hiddenFiat = sortedFiat.filter(r => !DEFAULT_FIAT.includes(r.currency_code) && !addedCurrencies.includes(r.currency_code));

  // Added currencies from hidden list
  const addedCryptoRates = sortedCrypto.filter(r => addedCurrencies.includes(r.currency_code));
  const addedFiatRates = sortedFiat.filter(r => addedCurrencies.includes(r.currency_code));

  // Combined display lists
  const displayCrypto = [...defaultVisibleCrypto, ...addedCryptoRates];
  const displayFiat = [...defaultVisibleFiat, ...addedFiatRates];

  const openAddModal = (type) => {
    setAddingType(type);
    setShowAddModal(true);
    // Smooth scroll to show the rate board area where modal appears
    const rateBoard = document.getElementById('rates');
    if (rateBoard) {
      const offset = rateBoard.offsetTop - 100; // 100px above the rate board
      window.scrollTo({ top: offset, behavior: 'smooth' });
    }
  };

  const addCurrency = (code) => {
    setAddedCurrencies(prev => [...prev, code]);
    setShowAddModal(false);
  };

  const availableToAdd = addingType === 'crypto' ? hiddenCrypto : hiddenFiat;

  return (
    <div className="rate-board mb-40 md:mb-24" id="rates" data-testid="rate-board">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-1">Exchange Rates</h2>
          <p className="text-blue-200/50 text-sm">Rates in CAD</p>
        </div>
        <button
          onClick={fetchRates}
          disabled={loading}
          className="p-3 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all disabled:opacity-50"
          data-testid="refresh-rates-btn"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Table Header */}
      <div className="hidden md:grid grid-cols-3 gap-4 px-5 py-3 text-xs font-semibold text-blue-200/50 uppercase tracking-wider border-b border-white/10 mb-3">
        <div>Currency</div>
        <div className="text-right">We Buy</div>
        <div className="text-right">We Sell</div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden grid grid-cols-3 gap-2 px-3 py-2 text-xs font-semibold text-blue-200/50 uppercase tracking-wider border-b border-white/10 mb-2">
        <div>Currency</div>
        <div className="text-right">We Buy</div>
        <div className="text-right">We Sell</div>
      </div>

      {/* Crypto Section */}
      {displayCrypto.length > 0 && (
        <div className="mb-6">
          <div className="px-4 py-2">
            <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Cryptocurrency</span>
          </div>
          <div className="space-y-2">
            {displayCrypto.map((rate) => (
              <RateRow key={rate.id} rate={rate} formatRate={formatRate} />
            ))}
          </div>
          {/* Add Cryptocurrency Button */}
          {hiddenCrypto.length > 0 && (
            <button 
              className="add-currency-btn mt-3" 
              data-testid="add-crypto-btn"
              onClick={() => openAddModal('crypto')}
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm font-medium">Add Cryptocurrency</span>
            </button>
          )}
        </div>
      )}

      {/* Fiat Section */}
      {displayFiat.length > 0 && (
        <div>
          <div className="px-4 py-2">
            <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">Fiat Currency</span>
          </div>
          <div className="space-y-2">
            {displayFiat.map((rate) => (
              <RateRow key={rate.id} rate={rate} formatRate={formatRate} />
            ))}
          </div>
          {/* Add Currency Button */}
          {hiddenFiat.length > 0 && (
            <button 
              className="add-currency-btn mt-3" 
              data-testid="add-fiat-btn"
              onClick={() => openAddModal('fiat')}
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm font-medium">Add Currency</span>
            </button>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && rates.length === 0 && (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="rate-row animate-pulse grid grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10"></div>
                <div className="h-4 w-16 bg-white/10 rounded"></div>
              </div>
              <div className="h-4 w-20 bg-white/10 rounded ml-auto"></div>
              <div className="h-4 w-20 bg-white/10 rounded ml-auto"></div>
            </div>
          ))}
        </div>
      )}

      {/* No rates message */}
      {!loading && rates.length === 0 && (
        <div className="text-center py-8">
          <p className="text-blue-200/50">No rates available. Please check back later.</p>
        </div>
      )}

      {/* Last Update */}
      {lastUpdate && rates.length > 0 && (
        <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
          <p className="text-xs text-blue-200/40">Updated: {lastUpdate.toLocaleTimeString()}</p>
          <p className="text-xs text-blue-200/40">Rates subject to change</p>
        </div>
      )}

      {/* Add Currency Modal - Fixed center of screen */}
      {showAddModal && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4" 
          onClick={() => setShowAddModal(false)}
        >
          <div 
            className="bg-[#001435] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Fixed Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h3 className="text-xl font-bold text-white">
                Add {addingType === 'crypto' ? 'Cryptocurrency' : 'Currency'}
              </h3>
              <button 
                onClick={() => setShowAddModal(false)}
                className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1 p-4 modal-scroll">
              <div className="space-y-2">
                {availableToAdd.map((currency) => (
                  <button
                    key={currency.id}
                    onClick={() => addCurrency(currency.currency_code)}
                    className="w-full flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-blue-500/50 transition-all"
                    data-testid={`add-currency-${currency.currency_code.toLowerCase()}`}
                  >
                    <img 
                      src={getCurrencyFlag(currency.currency_code)} 
                      alt={currency.currency_code}
                      className="w-10 h-10 rounded-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="text-left flex-1">
                      <span className="font-semibold text-white block">{currency.currency_code}</span>
                      <span className="text-blue-200/50 text-sm">{currency.currency_name}</span>
                    </div>
                    <Plus className="w-5 h-5 text-blue-400" />
                  </button>
                ))}
                
                {availableToAdd.length === 0 && (
                  <p className="text-center text-blue-200/50 py-4">All currencies have been added</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RateRow({ rate, formatRate }) {
  const flagUrl = getCurrencyFlag(rate.currency_code);

  return (
    <div 
      className="rate-row grid grid-cols-3 gap-2 md:gap-4 items-center"
      data-testid={`rate-row-${rate.currency_code.toLowerCase()}`}
    >
      <div className="flex items-center gap-2 md:gap-3">
        {flagUrl && (
          <img 
            src={flagUrl} 
            alt={rate.currency_code}
            className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover border border-white/20 flex-shrink-0"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <div className="min-w-0">
          <span className="font-semibold text-white block text-sm md:text-base">{rate.currency_code}</span>
          <span className="text-blue-200/40 text-xs block truncate hidden md:block">{rate.currency_name}</span>
        </div>
      </div>
      <div className="text-right">
        <span className="font-mono text-sm md:text-lg font-semibold text-emerald-400">
          {formatRate(rate.sell_rate, rate.currency_code)}
        </span>
      </div>
      <div className="text-right">
        <span className="font-mono text-sm md:text-lg font-semibold text-rose-400">
          {formatRate(rate.buy_rate, rate.currency_code)}
        </span>
      </div>
    </div>
  );
}
